import { createMuiTheme } from '@material-ui/core/styles';
import type { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import type { Theme } from '@material-ui/core';

const THEME_BASE = createMuiTheme({
  palette: {
    primary: {
      dark: '#8d7b50',
      main: '#af985e',
      contrastText: '#fff'
    },
    secondary: {
      main: '#bf0000',
      contrastText: '#fff'
    },
    text: {
      primary: '#404040',
      secondary: '#7d7d7d',
      disabled: '#aaa'
    },
    action: {
      selected: '#af985e'
    },
    background: {
      default: '#f5f5f5',
      paper: '#fff'
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 992,
      lg: 1280, // muiのデフォルト値
      xl: 1920 // muiのデフォルト値
    }
  }
});

const THEME_DEFAULT: ThemeOptions = {
  palette: { ...THEME_BASE.palette },
  typography: palette => ({
    fontFamily: 'Noto Sans Japanese,sans-serif',
    fontSize: 13,
    body1: {
      fontSize: 13
    },
    body2: {
      fontSize: 12
    },
    caption: {
      fontSize: 11.5
    },
    button: {
      fontSize: 13
    }
  }),
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 'bold',
        '&$disabled': {
          opacity: 0.5
        },
        paddingTop: 8,
        paddingBottom: 8,
        lineHeight: 1.5
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          // _typography.scssのaのスタイルに負けてしまうため付与。本来必要ない。
          color: (THEME_BASE.palette.primary as any).contrastText
        },
        '&:hover,&:active': {
          boxShadow: 'none'
        }
      },
      sizeLarge: {
        fontSize: 20
      },
      outlined: {
        paddingTop: 7,
        paddingBottom: 7
      }
    },
    MuiDialog: {
      paperScrollPaper: {
        backgroundColor: THEME_BASE.palette.background.default
      }
    },
    MuiDialogContent: {
      root: {
        paddingRight: 16,
        paddingLeft: 16,
        '& p': {
          margin: 0
        }
      }
    },
    MuiDialogActions: {
      root: {
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16
      }
    }
  },
  props: {
    MuiButtonBase: {
      disableRipple: true
    }
  }
};

export const THEME_AI_TRAVEL = createMuiTheme({
  ...THEME_DEFAULT
});

export const THEME_TABI_KOBO = createMuiTheme({
  ...THEME_DEFAULT,
  overrides: {
    ...THEME_DEFAULT.overrides,
    MuiButton: {
      ...THEME_DEFAULT.overrides?.MuiButton,
      contained: {
        ...THEME_DEFAULT.overrides?.MuiButton?.contained,
        /**
         * 旅工房はお客様の意向でサインイン前だけボタンを赤くする
         * https://aitravel.slack.com/archives/CETM6MMGX/p1636150432012400?thread_ts=1635917524.002700&cid=CETM6MMGX
         */
        '&.signin-button,&.pw-reset-button': {
          backgroundColor: '#f83933',
          borderRadius: 0,
          '&:hover': {
            backgroundColor: '#f83933'
          }
        }
      }
    },
    MuiLink: {
      root: {
        // 旅工房はお客様の意向でサインイン前だけ赤くする
        '&.reset-password-link,&.signin-form-link': {
          color: '#f83933',
          '&:hover': {
            color: '#f83933'
          }
        }
      }
    }
  }
});

export const THEME_MY_NAVI = createMuiTheme({
  ...THEME_DEFAULT,
  palette: {
    ...THEME_DEFAULT.palette,
    primary: {
      main: '#22bbff',
      contrastText: '#fff'
    },
    secondary: {
      main: '#ea3c3c',
      contrastText: '#fff'
    },
    text: {
      primary: '#333',
      secondary: '#7d7d7d',
      disabled: '#99CBE8'
    }
  },
  overrides: {
    MuiButton: {
      ...THEME_DEFAULT.overrides?.MuiButton,
      root: {
        ...THEME_DEFAULT.overrides?.MuiButton?.root,
        borderRadius: 50
      },
      containedPrimary: {
        backgroundColor: '#ffbd0d',
        '&:hover': {
          backgroundColor: '#b28409'
        }
      }
    }
  }
});

export const THEME_EJ = createMuiTheme({
  ...THEME_DEFAULT
});

const themeByServiceId: Record<number, Theme> = {
  1: THEME_AI_TRAVEL,
  2: THEME_TABI_KOBO,
  3: THEME_MY_NAVI,
  4: THEME_EJ
};

export const getThemeByServiceId = (serviceId: number) => themeByServiceId[serviceId] ?? themeByServiceId[1];

/**
 * サービスIDに基づいてテーマクラス名を取得する
 * styledComponentからのwithThemeを使用したテーマクラスの取得から脱却したい場合にこちらを使用する想定
 * @param serviceId サービスID (1: AI Travel, 2: 旅工房, 3: Mynavi)
 * @returns テーマクラス名
 */
export const getThemeClass = (serviceId: number): string => {
  switch (serviceId) {
    case 1:
      return 'aitravel';
    case 2:
      return 'tabikobo';
    case 3:
      return 'mynavi';
    default:
      return 'aitravel';
  }
};
